import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useSelector } from "react-redux";
import { gsap } from "gsap";
import { usePostHog } from "posthog-js/react";
import { captureEvent, captureError } from "../utils/posthogUtils";
import { POSTHOG_EVENTS } from "../constants/posthog-events";
import {
    Stack,
    Alert,
    Typography,
    Box,
    Grid,
} from "@mui/material";
import { FaUsers } from "react-icons/fa";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { useForm, Controller } from "react-hook-form";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import axiosInstance from "../api/axiosInstance";
import {
    buildEmployeeAssigneeOption,
    buildEmployeeAssigneeOptionFromAssignee,
    filterEmployeeAssigneeOptions,
    renderEmployeeAssigneeOption,
} from "./common/EmployeeAssigneeOption";
import UncontrolledAutocomplete from "./Custom/UncontrolledAutocomplete";
import AsyncProjectAutocomplete from "./Custom/AsyncProjectAutocomplete";
import CustomButton from "./Custom/CustomButton";
import CustomInput from "./Custom/CustomInput";
import CustomTextArea from "./Custom/CustomTextArea";
import CustomDatePicker from "./Custom/CustomDatePicker";
import CustomUpload from "./Custom/CustomUpload";
import "./ModalStyles.css";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const normalizeDateForDayjs = (value) => {
    if (!value) return null;
    const d = dayjs(value);
    return d.isValid() ? d : null;
};

const STATUS_OPTIONS = [
    { value: "pending", label: "Pending" },
    { value: "planned", label: "Planned" },
    { value: "WIP", label: "WIP" },
    { value: "completed", label: "Completed" },
    { value: "revalidate", label: "Revalidate" },
    { value: "reopen", label: "Reopen" },
];

const PRIORITY_OPTIONS = [
    { value: "urgent", label: "Urgent" },
    { value: "high", label: "High" },
    { value: "medium", label: "Medium" },
    { value: "low", label: "Low" },
];

const LISTBOX_PROPS = {
    listbox: { style: { fontSize: "12px" } },
    paper: { sx: { fontSize: "12px" } },
};

const ATTACHMENT_API_URL = "https://tb-api-134882194511.us-central1.run.app/api/tasks/attachment";

// ─────────────────────────────────────────────────────────────────────────────
// EditTaskModal
// ─────────────────────────────────────────────────────────────────────────────

const EditTaskModal = ({ open, onClose, onTaskUpdated, task, employees = [], analyticsSource }) => {
    const overlayRef = useRef(null);
    const contentRef = useRef(null);
    const posthog = usePostHog();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const authUser = useSelector((state) => state.auth.user);
    const currentUserId = authUser?.userId || authUser?.user_id || authUser?.id || authUser?.sub;

    const reduxEmployees = useSelector((state) => state.employees.employees);
    const reduxProjects = useSelector((state) => state.projects.allProjects);

    const { control, handleSubmit, reset, setValue, watch } = useForm({
        defaultValues: {
            title: "",
            priority: null,
            project: null,
            startDate: null,
            targetDate: null,
            description: "",
            status: STATUS_OPTIONS[0],
            assignees: [],
            attachments: []
        },
        shouldFocusError: true
    });

    const currentAssignees = watch("assignees") || [];
    const startDateValue = watch("startDate");
    const targetDateValue = watch("targetDate");

    const isMeSelected = useMemo(() => {
        if (!currentUserId || !Array.isArray(currentAssignees)) return false;
        return currentAssignees.some(a => String(a.value) === String(currentUserId));
    }, [currentAssignees, currentUserId]);

    const projectId = watch("project")?.value;
    const isInitialGrouped = useMemo(() => Array.isArray(task?.assignees) && task.assignees.length > 1, [task?.assignees]);

    const employeeList = useMemo(() => {
        if (Array.isArray(employees) && employees.length > 0) return employees;
        return reduxEmployees || [];
    }, [employees, reduxEmployees]);

    const employeeOptions = useMemo(
        () => employeeList.map(buildEmployeeAssigneeOption),
        [employeeList]
    );

    const projectOptions = useMemo(() => {
        return (reduxProjects || []).map((p) => ({ value: String(p.id), label: p.name || `Project #${p.id}` }));
    }, [reduxProjects]);

    // ── Sync form when task changes ───────────────────────────────────────────
    useEffect(() => {
        if (task && open) {
            const assigneeIds = [];
            if (Array.isArray(task.assignees)) {
                task.assignees.forEach((a) => {
                    const id = a?.userId ?? a?.user_id ?? a?.assigned_to ?? a?.id;
                    if (id != null) assigneeIds.push(String(id));
                });
            }
            if (task.assigned_to != null) assigneeIds.push(String(task.assigned_to));
            const uniqueAssigneeIds = Array.from(new Set(assigneeIds));

            const taskAssignees = uniqueAssigneeIds.map(id => {
                const found = employeeOptions.find(opt => opt.value === id);
                if (found) return found;
                // Fallback for names not in employee list
                const hist = task.assignees?.find(a => String(a?.userId ?? a?.user_id ?? a?.assigned_to ?? a?.id) === id);
                return buildEmployeeAssigneeOptionFromAssignee(hist, id);
            });

            const rawProjectId = task.project_id ?? task.projectId ??
                (typeof task.project === "object" && task.project ? task.project.id ?? task.project.project_id : undefined);

            const matchingProject = projectOptions.find(p => p.value === String(rawProjectId)) ||
                (rawProjectId ? { value: String(rawProjectId), label: task.project_name || task?.project?.name || `Project #${rawProjectId}` } : null);

            reset({
                title: task.title || "",
                priority: PRIORITY_OPTIONS.find((o) => o.value?.toLowerCase() === task.priority?.toLowerCase()) ?? null,
                project: matchingProject,
                startDate: normalizeDateForDayjs(task.start_date),
                targetDate: normalizeDateForDayjs(task.due_date),
                description: task.originalDescription ?? task.description ?? "",
                status: STATUS_OPTIONS.find((o) => o.value?.toLowerCase() === (task.status || task.status_display || "").toLowerCase()) ?? STATUS_OPTIONS[0],
                assignees: taskAssignees,
                attachments: []
            });
            setError("");
        }
    }, [task, open, employeeOptions, projectOptions, reset]);

    // ── GSAP animation on open/close ──────────────────────────────────────────
    useEffect(() => {
        if (!overlayRef.current || !contentRef.current) return;
        if (open) {
            gsap.set(overlayRef.current, { display: "flex" });
            gsap.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: "power2.out" });
            gsap.fromTo(contentRef.current,
                { opacity: 0, y: -40, scale: 0.95 },
                { opacity: 1, y: 0, scale: 1, duration: 0.35, ease: "back.out(1.7)", delay: 0.05 }
            );
        } else {
            gsap.to(contentRef.current, { opacity: 0, y: -20, scale: 0.97, duration: 0.18, ease: "power2.in" });
            gsap.to(overlayRef.current, {
                opacity: 0, duration: 0.25, ease: "power2.in", delay: 0.08,
                onComplete: () => gsap.set(overlayRef.current, { display: "none" }),
            });
        }
    }, [open]);

    const handleClose = () => {
        gsap.to(contentRef.current, { opacity: 0, y: -20, scale: 0.97, duration: 0.18, ease: "power2.in" });
        gsap.to(overlayRef.current, {
            opacity: 0, duration: 0.25, ease: "power2.in", delay: 0.08,
            onComplete: () => { gsap.set(overlayRef.current, { display: "none" }); onClose(); },
        });
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const onActualSubmit = async (data) => {
        setError("");
        if (!task?.id) {
            const msg = "Task reference is missing.";
            setError(msg);
            toast.error(msg);
            return;
        }

        const assigneeIds = data.assignees.map((o) => String(o.value)).filter(Boolean).sort();
        if (assigneeIds.length === 0) {
            const msg = "Please select at least one assignee.";
            setError(msg);
            toast.error(msg);
            return;
        }

        setLoading(true);
        try {
            const currentUser = JSON.parse(localStorage.getItem("user")) || {};
            const effectiveUserId = currentUserId || currentUser?.userId || currentUser?.id;
            const payload = {};
            let hasChanges = false;

            if (data.title !== (task.title || "")) {
                payload.title = data.title;
                hasChanges = true;
            }

            const originalPriority = task.priority?.toLowerCase() || null;
            const newPriority = data.priority?.value || null;
            if (newPriority !== originalPriority) {
                payload.priority = newPriority;
                hasChanges = true;
            }

            const rawProjectId = task.project_id ?? task.projectId ??
                (typeof task.project === "object" && task.project ? task.project.id ?? task.project.project_id : undefined);
            const newProjectId = data.project?.value || null;
            if (String(newProjectId || "") !== String(rawProjectId || "")) {
                payload.project_id = newProjectId;
                hasChanges = true;
            }

            const originalDueDate = task.due_date ? dayjs(task.due_date).format("YYYY-MM-DD") : null;
            const newDueDate = data.targetDate ? data.targetDate.format("YYYY-MM-DD") : null;
            if (newDueDate !== originalDueDate) {
                payload.due_date = newDueDate;
                hasChanges = true;
            }

            const originalStartDate = task.start_date ? dayjs(task.start_date).format("YYYY-MM-DD") : null;
            const newStartDate = data.startDate ? data.startDate.format("YYYY-MM-DD") : null;
            if (newStartDate !== originalStartDate) {
                payload.start_date = newStartDate;
                hasChanges = true;
            }

            const originalDesc = task.originalDescription ?? task.description ?? "";
            if (data.description !== originalDesc) {
                payload.description = data.description;
                hasChanges = true;
            }

            const originalAssigneeIds = [];
            if (Array.isArray(task.assignees)) {
                task.assignees.forEach((a) => {
                    const id = a?.userId ?? a?.user_id ?? a?.assigned_to ?? a?.id;
                    if (id != null) originalAssigneeIds.push(String(id));
                });
            }
            if (task.assigned_to != null) originalAssigneeIds.push(String(task.assigned_to));
            const uniqueOriginalAssigneeIds = Array.from(new Set(originalAssigneeIds)).sort();

            if (JSON.stringify(assigneeIds) !== JSON.stringify(uniqueOriginalAssigneeIds)) {
                payload.assignees = assigneeIds;
                payload.assigned_to = assigneeIds[0] || null;
                hasChanges = true;
            }

            const originalStatus = (task.status || task.status_display || "pending").toString().toLowerCase();
            const newStatus = (data.status?.value || "pending").toString().toLowerCase();
            if (newStatus !== originalStatus) {
                payload.status = data.status?.value || "pending";
                hasChanges = true;
            }

            payload.group_id = task?.group_id || null;
            payload.currentUserId = effectiveUserId;

            const hasAttachments = data.attachments && data.attachments.length > 0;

            if (!hasChanges && !hasAttachments) {
                toast.info("No changes to update.");
                setLoading(false);
                handleClose();
                return;
            }

            if (hasChanges || hasAttachments) {
                const formData = new FormData();

                // Append text/json fields
                if (hasChanges) {
                    Object.entries(payload).forEach(([key, value]) => {
                        if (value !== null && value !== undefined) {
                            if (Array.isArray(value)) {
                                formData.append(key, JSON.stringify(value));
                            } else {
                                formData.append(key, value);
                            }
                        }
                    });
                }

                // Append taskAttachments
                if (hasAttachments) {
                    data.attachments.forEach((file) => {
                        formData.append("taskAttachments", file);
                    });
                }

                const isGrouped = Array.isArray(task?.assignees) && task.assignees.length > 1;
                const url = `/api/task/${task.id}${isGrouped ? "?is_grouped=1" : ""}`;
                await axiosInstance.put(url, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }

            const baseChangedFields = Object.keys(payload).filter(
                (k) => k !== "currentUserId" && k !== "group_id"
            );
            const fieldsChanged = [...baseChangedFields];
            if (hasAttachments && !fieldsChanged.includes("attachments")) {
                fieldsChanged.push("attachments");
            }

            captureEvent(posthog, POSTHOG_EVENTS.TASK_UPDATED, {
                task_id: task.id,
                title: data.title,
                fields_changed: fieldsChanged,
                attachments_added_count: hasAttachments ? data.attachments.length : 0,
                updated_by: effectiveUserId,
                ...(analyticsSource ? { source: analyticsSource } : {}),
            });

            onTaskUpdated?.({
                task,
                ...(payload.status ? { newStatus: payload.status } : {}),
            });
            handleClose();
        } catch (err) {
            captureError(posthog, POSTHOG_EVENTS.TASK_UPDATE_FAILED, err, {
                task_id: task?.id,
                title: data.title,
            });
            const errorMsg = err?.response?.data?.message || err.message || "Something went wrong.";
            setError(errorMsg);
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            ref={overlayRef}
            className="modal-overlay-base"
            style={{ display: "none" }}
        >
            <div
                ref={contentRef}
                className="modal-content-base"
                style={{ maxWidth: 700 }}
            >
                <div className="modal-header-base">
                    <h3 className="modal-title-base">Edit Task</h3>
                    <button className="modal-close-base" onClick={handleClose} title="Close">
                        <CloseIcon fontSize="small" />
                    </button>
                </div>

                <div className="modal-body-base">
                    <form id="edit-task-form" onSubmit={handleSubmit(onActualSubmit)}>
                        <Stack spacing={2} sx={{ width: "100%" }}>
                            {error && <Alert severity="error" sx={{ fontSize: "0.82rem" }}>{error}</Alert>}
                            {isInitialGrouped && (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 1.5,
                                    borderRadius: '10px',
                                    bgcolor: 'rgba(37, 99, 235, 0.04)',
                                    border: '1px solid rgba(37, 99, 235, 0.1)',
                                    mb: 1
                                }}>
                                    <FaUsers style={{ color: '#2563eb', fontSize: '18px' }} />
                                    <Typography sx={{ fontSize: '0.82rem', color: '#1e293b', fontWeight: 500 }}>
                                        <b>Shared Task:</b> Updates made here will synchronize across all assigned members.
                                    </Typography>
                                </Box>
                            )}
                            <CustomInput
                                control={control}
                                name="title"
                                label="Task Title *"
                                rules={{ required: "Task Title is required" }}
                            />

                            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Controller
                                        name="priority"
                                        control={control}
                                        rules={{ required: "Priority is required" }}
                                        render={({ field: { ref, ...field }, fieldState: { error: e } }) => (
                                            <UncontrolledAutocomplete
                                                {...field}
                                                ref={ref}
                                                label="Priority *"
                                                options={PRIORITY_OPTIONS}
                                                error={e?.message}
                                                slotProps={LISTBOX_PROPS}
                                                fullWidth
                                            />
                                        )}
                                    />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Controller
                                        name="project"
                                        control={control}
                                        render={({ field: { ref, ...field }, fieldState: { error: e } }) => (
                                            <AsyncProjectAutocomplete
                                                {...field}
                                                ref={ref}
                                                label="Project"
                                                error={e?.message}
                                            />
                                        )}
                                    />
                                </Box>
                            </Stack>

                            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <CustomDatePicker
                                        control={control}
                                        name="startDate"
                                        label="Start Date"
                                        maxDate={targetDateValue}
                                        rules={{ required: "Start Date is required" }}
                                        clearable={true}
                                    />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <CustomDatePicker
                                        control={control}
                                        name="targetDate"
                                        label="Target End Date *"
                                        rules={{ required: "Target End Date is required" }}
                                        minDate={startDateValue}
                                        clearable={true}
                                    />
                                </Box>
                            </Stack>

                            {/* Status full width */}
                            <Box sx={{ width: '100%' }}>
                                <Controller
                                    name="status"
                                    control={control}
                                    render={({ field: { ref, ...field }, fieldState: { error: e } }) => (
                                        <UncontrolledAutocomplete
                                            {...field}
                                            ref={ref}
                                            label="Status"
                                            options={STATUS_OPTIONS}
                                            error={e?.message}
                                            slotProps={LISTBOX_PROPS}
                                            disableClearable
                                            fullWidth
                                        />
                                    )}
                                />
                            </Box>

                            <Box sx={{ width: '100%', mt: 1.5 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', alignItems: 'center' }}>
                                    <span style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>
                                        Assign To:
                                    </span>
                                    {currentUserId && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const me = employeeOptions.find(opt => String(opt.value) === String(currentUserId));
                                                if (!me) {
                                                    toast.warning("Current user not found in employee list");
                                                    return;
                                                }
                                                if (isMeSelected) {
                                                    setValue("assignees", currentAssignees.filter(a => String(a.value) !== String(currentUserId)));
                                                } else {
                                                    setValue("assignees", [...currentAssignees, me]);
                                                }
                                            }}
                                            style={{
                                                background: isMeSelected ? "rgba(37, 99, 235, 0.08)" : "none",
                                                border: isMeSelected ? "1px solid rgba(37, 99, 235, 0.3)" : "none",
                                                color: "#2563eb",
                                                fontSize: "10px",
                                                cursor: "pointer",
                                                padding: "2px 8px",
                                                borderRadius: "6px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                fontWeight: 600,
                                                transition: "all 0.2s ease"
                                            }}
                                        >
                                            <PersonIcon sx={{ fontSize: "14px" }} />
                                            {isMeSelected ? "Unassign me" : "Assign to me"}
                                        </button>
                                    )}
                                </div>
                                <Controller
                                    name="assignees"
                                    control={control}
                                    rules={{ required: "At least one assignee is required" }}
                                    render={({ field: { ref, ...field }, fieldState: { error: e } }) => (
                                        <UncontrolledAutocomplete
                                            {...field}
                                            ref={ref}
                                            label="Assign Employee(s)"
                                            options={employeeOptions}
                                            multiple
                                            disableCloseOnSelect
                                            error={e?.message}
                                            getOptionLabel={(opt) => opt?.name ?? opt?.label ?? ""}
                                            renderOption={renderEmployeeAssigneeOption}
                                            filterOptions={filterEmployeeAssigneeOptions}
                                            isOptionEqualToValue={(opt, val) => opt.value === val.value}
                                            slotProps={LISTBOX_PROPS}
                                            fullWidth
                                        />
                                    )}
                                />
                            </Box>

                            <CustomTextArea
                                control={control}
                                name="description"
                                label="Description"
                                rows={3}
                            />

                            {/* Attachments Option */}
                            <div className="custom-file-upload-section">
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
                                    Attachments
                                </Typography>
                                <CustomUpload
                                    control={control}
                                    name="attachments"
                                    isMultiple={true}
                                    extensions={["jpg", "jpeg", "png", "pdf", "doc", "docx", "xls", "xlsx", "csv", "zip"]}
                                    labelSize="small"
                                    size="small"
                                    maxFileSize={15728640}
                                />
                            </div>

                        </Stack>
                    </form>
                </div>

                <div className="modal-footer-base" style={{ justifyContent: "flex-end", gap: 12 }}>
                    <CustomButton
                        variant="outlined"
                        color="inherit"
                        onClick={handleClose}
                        disabled={loading}
                        sx={{ textTransform: "none", borderRadius: 2, minWidth: 90 }}
                    >
                        Cancel
                    </CustomButton>
                    <CustomButton
                        type="submit"
                        form="edit-task-form"
                        variant="contained"
                        loading={loading}
                        loadingText="Updating…"
                        startIcon={<SaveIcon />}
                        sx={{ textTransform: "none", borderRadius: 2, minWidth: 130 }}
                    >
                        Update Task
                    </CustomButton>
                </div>
            </div>
        </div>
    );
};

export default EditTaskModal;
